import { useEffect, useRef } from 'react';
import useGameStore from '../store/gameStore';
import { useSound } from './useSound';

/**
 * Auto Bet: automatically places bets and continues steps.
 * Strategy: continue until step >= stopStep, then cash out.
 */
export function useAutoBet({ placeBet, takeStep, cashOut }) {
  const {
    autoBet, autoBetRemaining,
    autoBetStopOnWin, autoBetStopOnLoss,
    gameState, betAmount, difficulty,
    lastOutcome,
    setAutoBet,
  } = useGameStore();
  const { play } = useSound();

  const stopStepRef = useRef(3); // default: cash out after step 3

  // Always holds the latest values so the effect closure never goes stale
  const latestRef = useRef({});
  latestRef.current = {
    placeBet, takeStep, cashOut, play, setAutoBet,
    betAmount, difficulty,
    autoBetStopOnWin, autoBetStopOnLoss,
    lastOutcome, autoBetRemaining,
  };

  useEffect(() => {
    if (!autoBet) return;
    const r = latestRef.current;

    if (gameState === 'idle' && r.autoBetRemaining > 0) {
      const timer = setTimeout(() => {
        r.placeBet(r.betAmount, r.difficulty);
      }, 480);
      return () => clearTimeout(timer);
    }

    if (gameState === 'round_started' || gameState === 'step_success') {
      const store = useGameStore.getState();
      if (store.currentStep >= stopStepRef.current) {
        r.cashOut();
      } else {
        // step 1 success has no visual movement (rabbit stays on card 0), so skip the wait
        const delay = gameState === 'round_started' ? 450 : store.currentStep === 1 ? 300 : 576;
        const timer = setTimeout(() => r.takeStep(), delay);
        return () => clearTimeout(timer);
      }
    }

    if (gameState === 'step_failed' || gameState === 'cashed_out') {
      if (gameState === 'step_failed') r.play('lose');
      if (gameState === 'cashed_out')  r.play('cashout');

      const shouldStop =
        (r.autoBetStopOnWin  && r.lastOutcome === 'cashout') ||
        (r.autoBetStopOnLoss && r.lastOutcome === 'lose')    ||
        r.autoBetRemaining <= 1;

      // Hold on crash so the crash effect is visible before resetting
      const delay = gameState === 'step_failed' ? 720 : 240;

      const timer = setTimeout(() => {
        if (shouldStop) {
          r.setAutoBet({ autoBet: false, autoBetRemaining: 0 });
        } else {
          r.setAutoBet({ autoBetRemaining: r.autoBetRemaining - 1 });
        }
        useGameStore.getState().resetRound();
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [autoBet, gameState, autoBetRemaining]); // eslint-disable-line react-hooks/exhaustive-deps

  function startAutoBet({ count, stopStep = 3, stopOnWin = false, stopOnLoss = false }) {
    stopStepRef.current = stopStep;
    setAutoBet({
      autoBet: true,
      autoBetCount: count,
      autoBetRemaining: count,
      autoBetStopOnWin: stopOnWin,
      autoBetStopOnLoss: stopOnLoss,
    });
  }

  function stopAutoBet() {
    setAutoBet({ autoBet: false, autoBetRemaining: 0 });
  }

  return { startAutoBet, stopAutoBet };
}
